import { useCallback, useEffect, useMemo } from 'react'

import { useLazyGetAllConfiguredServicesQuery } from 'api/configured-service/searchConfiguredServices'
import { useLazyGetAccountContainersQuery } from 'api/inventory/accountContainer'
import T from 'T'
import { handleError } from 'utils/error'

import type { AccountSettleableRow } from 'components/inventory/containers/configured-service/deactivation/DeactivateAccountDrawer'
import type { PlaceholderKind } from 'types/entities/shared'

const ACTIVE = 'Active'

interface AccountContainerRow {
  id?: string
  name?: string | null
  serialNumber?: string | null
  services?: string[] | null
  placeholder?: boolean | null
  placeholderType?: PlaceholderKind | null
  customerOwned?: boolean | null
  containerType?: { name?: string | null } | null
  location?: { locationId?: string | null; locationName?: string | null; line1?: string | null } | null
}

interface ConfiguredServiceRow {
  configuredServiceId?: string
  serviceName?: string | null
  status?: string | null
}

// A container the rep can tell apart. The generic rows carry neither a name nor a serial, so their
// type stands in rather than an empty line.
const identityOf = (container: AccountContainerRow) => container.name || container.containerType?.name || T.CONTAINER

// Active services first, then the ones already ended, then whatever sits on no service at all —
// which is where an account that predates this flow keeps its forgotten cans.
const rankOf = (service?: ConfiguredServiceRow) => {
  if (!service) return 2

  return service.status === ACTIVE ? 0 : 1
}

/**
 * Every container on the account, grouped by its service and ordered so the groups come out
 * contiguous. Deactivated services are in scope: nothing has ever asked what became of their cans.
 */
export const useAccountSettlementRows = (accountId?: string, isEnabled = false) => {
  const [getAccountContainers, { currentData: containerData, isFetching: isFetchingContainers, isError: isContainerError }] =
    useLazyGetAccountContainersQuery()
  const [getAllConfiguredServices, { currentData: serviceData, isFetching: isFetchingServices }] = useLazyGetAllConfiguredServicesQuery()

  const refetch = useCallback(() => {
    if (!accountId) return
    getAccountContainers({ accountId }).unwrap().catch(handleError)
    // No statuses, so deactivated services come back too — they are the whole point of asking here.
    getAllConfiguredServices({ accountId }).unwrap().catch(handleError)
  }, [accountId, getAccountContainers, getAllConfiguredServices])

  useEffect(() => {
    if (!isEnabled) return
    refetch()
  }, [isEnabled, refetch])

  const rows = useMemo<AccountSettleableRow[]>(() => {
    const containers = (containerData?.containers ?? []) as AccountContainerRow[]
    const services = (serviceData ?? []) as ConfiguredServiceRow[]
    const serviceById = new Map(
      services.filter(service => service.configuredServiceId).map(service => [service.configuredServiceId, service])
    )
    // Only worth naming a location when the account has more than one to confuse it with.
    const hasManyLocations = new Set(containers.map(container => container.location?.locationId).filter(Boolean)).size > 1

    const mapped = containers
      .filter(container => Boolean(container.id))
      .map(container => {
        const serviceId = (container.services ?? [])[0]
        const service = serviceId ? serviceById.get(serviceId) : undefined

        return {
          ...container,
          id: String(container.id),
          containerName: identityOf(container),
          groupKey: serviceId ?? 'unassigned',
          groupLabel: service?.serviceName || (serviceId ? T.SERVICE : T.NOT_ON_A_SERVICE),
          locationLabel: hasManyLocations ? container.location?.locationName || container.location?.line1 || '' : '',
          isServiceDeactivated: Boolean(service) && service?.status !== ACTIVE,
          rank: rankOf(service),
        }
      })
      .sort(
        (left, right) =>
          left.rank - right.rank ||
          left.locationLabel.localeCompare(right.locationLabel) ||
          String(left.groupLabel).localeCompare(String(right.groupLabel)) ||
          String(left.groupKey).localeCompare(String(right.groupKey))
      )

    // Repeating an address down every group turns the one caption that matters into wallpaper.
    return mapped.map((row, index) => ({
      ...row,
      groupCaption:
        [
          row.isServiceDeactivated ? T.SERVICE_ALREADY_DEACTIVATED : '',
          row.locationLabel === mapped[index - 1]?.locationLabel ? '' : row.locationLabel,
        ]
          .filter(Boolean)
          .join(' · ') || null,
    }))
  }, [containerData, serviceData])

  return {
    rows,
    isFetching: isFetchingContainers || isFetchingServices,
    // Services only decorate the grouping; losing the containers is what leaves the question unasked.
    hasError: isContainerError,
    refetch,
  }
}
